// Utilitarios da Fiscalizacao mensal (RMA) no Painel da Devedora.
import axios from "axios";

export const MAX_FILE_BYTES = 200 * 1024 * 1024;

// Formatos do catalogo do RMA (Rma.SUPPORTED_EXTENSIONS na API); vale quando o
// tipo de documento nao restringe os formatos.
export const UPLOAD_EXTENSIONS = ["pdf", "xlsx", "xls", "csv", "txt", "png", "jpg", "jpeg", "zip"];

// Envio do arquivo para a API (multipart, campo `file`); a API repassa ao storage
// em stream. `onProgress` recebe { loaded, total, percent, estimated } dos bytes
// enviados (estimated: segundos restantes, quando o navegador permite calcular).
// Sem timeout: um arquivo de 200 MB leva minutos em conexao lenta.
export function uploadFile(url, file, { onProgress, signal } = {}) {
  const form = new FormData();
  form.append("file", file, file.name);
  return axios.post(url, form, {
    timeout: 0,
    signal,
    onUploadProgress: (event) => {
      if (!onProgress) return;
      const total = event.total || file.size || 0;
      const ratio = total ? Math.min(1, event.loaded / total) : 0;
      onProgress({
        loaded: Math.round(ratio * (file.size || total)),
        total: file.size || total,
        percent: Math.floor(ratio * 100),
        estimated: Number.isFinite(event.estimated) ? event.estimated : null,
      });
    },
  });
}

// Segundos restantes -> "falta menos de 1 min" / "faltam cerca de 3 min".
export function formatRemaining(seconds) {
  if (!Number.isFinite(seconds) || seconds < 5) return "";
  if (seconds < 60) return "falta menos de 1 min";
  const minutes = Math.round(seconds / 60);
  return minutes === 1 ? "falta cerca de 1 min" : `faltam cerca de ${minutes} min`;
}

// Status do item: texto e cor do PaBadge.
export const ITEM_STATUS = {
  pending: { label: "Pendente", tone: "neutral" },
  sent: { label: "Enviado", tone: "info" },
  accepted: { label: "Conferido", tone: "success" },
  returned: { label: "Devolvido para ajuste", tone: "danger" },
  not_applicable: { label: "Não se aplica", tone: "neutral" },
};

// Situacao da competencia.
export const COMPETENCE_STATUS = {
  awaiting: { label: "Aguardando envio", tone: "warning" },
  receiving: { label: "Recebendo", tone: "info" },
  complete: { label: "Completo", tone: "success" },
};

export function itemStatus(status) {
  return ITEM_STATUS[status] || { label: status || "—", tone: "neutral" };
}

export function competenceStatus(status) {
  return COMPETENCE_STATUS[status] || { label: status || "—", tone: "neutral" };
}

// A API responde erros como { content: { success: false, error: { message } } }.
export function errorMessage(error, fallback = "Não foi possível concluir a operação. Tente novamente.") {
  const data = error && error.response && error.response.data;
  const content = (data && data.content) || (error && error.content);
  if (content && content.error && content.error.message) return content.error.message;
  if (error && error.error && error.error.message) return error.error.message;
  // 413 sem corpo da API: o proxy (Cloudflare) recusou o envio pelo tamanho.
  if (error && error.response && error.response.status === 413) return "O arquivo é maior que o limite de envio (200 MB por arquivo).";
  if (error && error.request && !error.response) return "Sem conexão com o servidor. Verifique a internet e tente de novo.";
  return fallback;
}

export function notifyError(vm, error, fallback) {
  vm.$dialog.notify.error(errorMessage(error, fallback), { position: "top-right", timeout: 5000 });
}

export function notifySuccess(vm, message) {
  vm.$dialog.notify.success(message, { position: "top-right", timeout: 2500 });
}

// O banco devolve carimbos em UTC como "YYYY-MM-DD HH:mm:ss".
export function parseUtc(value) {
  if (!value) return null;
  const text = String(value);
  const date = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(text) ? new Date(`${text.replace(" ", "T")}Z`) : new Date(text);
  return Number.isNaN(date.getTime()) ? null : date;
}

// Carimbo UTC -> "08/10/2026 às 14:30" (horario de Brasilia).
export function formatDateTime(value) {
  const date = parseUtc(value);
  if (!date) return "—";
  const options = { timeZone: "America/Sao_Paulo" };
  const day = date.toLocaleDateString("pt-BR", options);
  const time = date.toLocaleTimeString("pt-BR", { ...options, hour: "2-digit", minute: "2-digit" });
  return `${day} às ${time}`;
}

// Data de calendario "YYYY-MM-DD" -> "20/07/2026".
export function formatDate(value) {
  if (!value) return "—";
  const text = String(value).slice(0, 10);
  return `${text.slice(8, 10)}/${text.slice(5, 7)}/${text.slice(0, 4)}`;
}

// Data de calendario "YYYY-MM-DD" -> "20/07".
export function formatDayMonth(value) {
  if (!value) return "—";
  const text = String(value).slice(0, 10);
  return `${text.slice(8, 10)}/${text.slice(5, 7)}`;
}

export function formatBytes(bytes) {
  const value = Number(bytes) || 0;
  if (value >= 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(1).replace(".", ",")} MB`;
  if (value >= 1024) return `${Math.round(value / 1024)} KB`;
  return `${value} B`;
}

export function formatExtensions(list) {
  return (list || []).map((ext) => String(ext).toUpperCase()).join(", ");
}

export function fileExtension(name) {
  const text = String(name || "");
  const index = text.lastIndexOf(".");
  return index > 0 ? text.slice(index + 1).toLowerCase() : "";
}

export function acceptedExtensions(item) {
  const list = item && item.document_type && item.document_type.accepted_extensions;
  return list && list.length ? list.map((ext) => String(ext).toLowerCase()) : UPLOAD_EXTENSIONS;
}

// Valor do atributo accept do <input type="file">: ".pdf,.xlsx".
export function acceptAttribute(extensions) {
  return (extensions || []).map((ext) => `.${ext}`).join(",");
}

// Mensagem do problema do arquivo, ou null se ele pode ser enviado.
export function validateFile(file, extensions) {
  if (!file) return "Selecione um arquivo.";
  const allowed = extensions && extensions.length ? extensions : UPLOAD_EXTENSIONS;
  if (!allowed.includes(fileExtension(file.name))) return `Formato não aceito. Envie ${formatExtensions(allowed)}.`;
  if (!Number(file.size)) return "O arquivo está vazio.";
  if (Number(file.size) > MAX_FILE_BYTES) return `O arquivo tem ${formatBytes(file.size)}; o limite é 200 MB por arquivo.`;
  return null;
}

export function fileIcon(name) {
  const ext = fileExtension(name);
  if (ext === "pdf") return "mdi-file-pdf-box";
  if (["xls", "xlsx", "csv"].includes(ext)) return "mdi-file-excel-outline";
  if (["png", "jpg", "jpeg", "webp"].includes(ext)) return "mdi-file-image-outline";
  if (ext === "zip") return "mdi-folder-zip-outline";
  return "mdi-file-document-outline";
}

// Contagem de itens: entregues = enviados + conferidos + "nao se aplica".
export function countItems(items) {
  const list = items || [];
  const missing = list.filter((item) => item.missing).length;
  return { total: list.length, missing, delivered: list.length - missing };
}

export function percent(counts) {
  if (!counts || !counts.total) return 0;
  return Math.round((Number(counts.delivered || 0) / Number(counts.total)) * 100);
}

// "3 de 6 documentos enviados"
export function deliveredText(counts) {
  const total = Number((counts && counts.total) || 0);
  const delivered = Number((counts && counts.delivered) || 0);
  return `${delivered} de ${total} ${total === 1 ? "documento enviado" : "documentos enviados"}`;
}

// As URLs assinadas da API ja pedem o download (Content-Disposition: attachment),
// entao o link na propria aba baixa o arquivo sem sair da tela.
export function downloadFromUrl(url, fileName) {
  if (!url) return;
  const link = document.createElement("a");
  link.href = url;
  link.rel = "noopener";
  if (fileName) link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
